import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, ClipboardCheck, CheckCircle2, AlertTriangle, Search, Undo2, Send, History } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { BRANDS, makeBrandResolver } from '@/config/stockBrands';
import { formatApiError } from '@/utils/apiError';

/**
 * Stock check workflow — BATCHED.
 *
 * The stock-taker walks the warehouse marking each product Correct or
 * Adjust; everything is held in local state until the sticky Submit bar
 * at the bottom is clicked. Then a single bulk POST hits the backend and
 * ONE summary email goes to orders@ + dan@.
 *
 * The page never reloads mid-take — counts, discrepancies and the visual
 * "done" badges are all driven from React state.
 */
export default function StockCheck() {
  const navigate = useNavigate();
  const { isStaff, user } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [categoryDocs, setCategoryDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [activeBrand, setActiveBrand] = useState('probound');
  const [adjustFor, setAdjustFor] = useState(null);

  // Local "pending" state — nothing hits the API until the user clicks the
  // sticky Submit button at the bottom. Keyed by product_id with one of:
  //   { kind: 'confirmed' }
  //   { kind: 'adjusted', qty: 5, reason: '...' }
  // Persisted per user in localStorage so a half-done check survives leaving the page.
  const storeKey = user ? `stockcheck-pending-${user.user_id}` : null;
  const [pending, setPendingState] = useState({});
  const [startedAt, setStartedAt] = useState(null);
  const setPending = useCallback((updater) => {
    setPendingState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (storeKey) {
        const started = Object.keys(next).length ? (startedAt || new Date().toISOString()) : null;
        if (started !== startedAt) setStartedAt(started);
        localStorage.setItem(storeKey, JSON.stringify({ pending: next, startedAt: started }));
      }
      return next;
    });
  }, [storeKey, startedAt]);
  useEffect(() => {
    if (!storeKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(storeKey) || 'null');
      if (saved?.pending && Object.keys(saved.pending).length) { setPendingState(saved.pending); setStartedAt(saved.startedAt || null); }
    } catch { /* ignore */ }
  }, [storeKey]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/stock/products`, { credentials: 'include' }),
        fetch(`${API_URL}/api/stock/categories`, { credentials: 'include' }),
      ]);
      if (!prodRes.ok) throw new Error('Failed to load products');
      if (!catRes.ok) throw new Error('Failed to load categories');
      setProducts(await prodRes.json());
      setCategoryDocs(await catRes.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    load();
  }, [isStaff, navigate, load]);

  const resolveBrand = useMemo(() => makeBrandResolver(categoryDocs), [categoryDocs]);

  const brandCounts = useMemo(() => {
    const out = { probound: 0, proepoxy: 0, prodecor: 0, misc: 0 };
    for (const p of products) out[resolveBrand(p.category)]++;
    return out;
  }, [products, resolveBrand]);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const inBrand = products.filter(p => resolveBrand(p.category) === activeBrand);
    const filtered = q
      ? inBrand.filter(p => p.name.toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q))
      : inBrand;
    const byCat = new Map();
    for (const p of filtered) {
      const c = p.category || 'Uncategorised';
      if (!byCat.has(c)) byCat.set(c, []);
      byCat.get(c).push(p);
    }
    return Array.from(byCat.entries());
  }, [products, filter, activeBrand, resolveBrand]);

  const confirmCorrect = (p) => {
    setPending(prev => ({ ...prev, [p.id]: { kind: 'confirmed' } }));
  };
  const undoRow = (p) => {
    setPending(prev => {
      const next = { ...prev };
      delete next[p.id];
      return next;
    });
  };
  const stageAdjustment = (p, qty, reason) => {
    setPending(prev => ({ ...prev, [p.id]: { kind: 'adjusted', qty, reason } }));
  };

  // Brand-scoped tally. The Submit button only enables once every product
  // in the active brand has been either marked Correct or Adjusted —
  // turning each brand tab into its own discrete stock-take. Pending
  // marks for other brands survive in `pending` if the user happens to
  // switch tabs, but the bar always reflects the current brand only.
  const brandIds = useMemo(
    () => products.filter(p => resolveBrand(p.category) === activeBrand).map(p => p.id),
    [products, resolveBrand, activeBrand],
  );
  const pendingCounts = useMemo(() => {
    let confirmed = 0;
    let adjusted = 0;
    for (const id of brandIds) {
      const v = pending[id];
      if (!v) continue;
      if (v.kind === 'confirmed') confirmed++;
      else if (v.kind === 'adjusted') adjusted++;
    }
    const marked = confirmed + adjusted;
    const total = brandIds.length;
    return { confirmed, adjusted, marked, total, remaining: Math.max(0, total - marked) };
  }, [pending, brandIds]);

  const submitAll = async () => {
    if (pendingCounts.marked === 0) {
      toast.error('Mark at least one item Correct or Adjust before submitting.');
      return;
    }
    if (pendingCounts.remaining > 0) {
      toast.error(`${pendingCounts.remaining} ${BRANDS.find(b => b.key === activeBrand)?.label} product${pendingCounts.remaining === 1 ? '' : 's'} still to mark — every product in the section must be done before you can submit.`);
      return;
    }
    // Only send rows for the active brand — keeps each brand tab a
    // discrete take so the summary email's scope matches the heading.
    // The whole brand must be marked; the backend enforces this too.
    const rows = brandIds
      .map(id => {
        const v = pending[id];
        if (!v) return null;
        return v.kind === 'confirmed'
          ? { product_id: id, action: 'count' }
          : { product_id: id, action: 'discrepancy', qty_on_hand: v.qty, reason: v.reason };
      })
      .filter(Boolean);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/check/submit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand_label: BRANDS.find(b => b.key === activeBrand)?.label,
          brand: activeBrand,
          rows,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      toast.success(`Submitted: ${data.confirmed} confirmed, ${data.adjustments} adjusted. Summary email sent.`);
      // Clear ONLY the rows we just submitted — preserves marks made on
      // other brand tabs that the user hasn't submitted yet, and leaves
      // any unmarked rows in this brand alone (partial submits are fine).
      setPending(prev => {
        const next = { ...prev };
        for (const id of brandIds) {
          if (next[id]) delete next[id];
        }
        return next;
      });
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const todayCount = useMemo(() => {
    const inBrand = products.filter(p => resolveBrand(p.category) === activeBrand);
    return inBrand.filter(p => (p.last_counted_at || '').slice(0, 10) === new Date().toISOString().slice(0, 10)).length;
  }, [products, resolveBrand, activeBrand]);

  const totalInBrand = brandCounts[activeBrand];

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} data-testid="check-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Stock Check</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">{todayCount} / {totalInBrand} confirmed today in {BRANDS.find(b => b.key === activeBrand)?.label} · submit at the end to email a single summary</p>
          </div>
          <Button onClick={() => navigate('/staff/stock/checks')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" data-testid="check-history-btn" aria-label="Stock Check History">
            <History className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">History</span>
          </Button>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Staff">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 py-5 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="check-brand-picker">
          {BRANDS.map(b => {
            const active = activeBrand === b.key;
            const count = brandCounts[b.key] || 0;
            return (
              <button
                key={b.key}
                onClick={() => setActiveBrand(b.key)}
                data-testid={`check-brand-${b.key}`}
                className={`px-3 py-2 rounded-lg text-sm font-bold border-2 transition-colors flex-1 sm:flex-none ${active ? 'text-white border-transparent shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
                style={active ? { backgroundColor: b.color, color: b.ink || '#fff' } : undefined}
              >
                {b.label}
                <span className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
              </button>
            );
          })}
        </div>

        <Card className="p-4">
          {pendingCounts.marked > 0 && pendingCounts.remaining > 0 && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex flex-wrap items-center gap-2" data-testid="check-in-progress">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span><strong>{BRANDS.find(b => b.key === activeBrand)?.label} check in progress</strong>{startedAt ? ` · started ${new Date(startedAt).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}` : ''} · {pendingCounts.marked} / {pendingCounts.total} marked. Finish every product in this section to submit — nothing is saved to stock until then.</span>
            </div>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              data-testid="check-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search products or categories…"
              className="pl-9"
            />
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : grouped.length === 0 ? (
          <Card className="p-10 text-center text-gray-500 italic">No products match that filter.</Card>
        ) : grouped.map(([category, items]) => (
          <Card key={category} data-testid={`check-cat-${category}`}>
            <div className="px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-gray-600 bg-gray-100 rounded-t-xl">{category}</div>
            <div className="divide-y divide-gray-100">
              {items.map(p => {
                const countedToday = (p.last_counted_at || '').slice(0, 10) === new Date().toISOString().slice(0, 10);
                const local = pending[p.id];
                const isConfirmed = local?.kind === 'confirmed';
                const isAdjusted = local?.kind === 'adjusted';
                return (
                  <div
                    key={p.id}
                    data-testid={`check-row-${p.id}`}
                    className={`p-3 flex flex-wrap items-center gap-3 transition-colors ${isConfirmed ? 'bg-emerald-50/60' : isAdjusted ? 'bg-amber-50/60' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{p.name}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {isConfirmed && (
                          <span data-testid={`check-tick-${p.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Marked correct
                          </span>
                        )}
                        {isAdjusted && (
                          <span data-testid={`check-adjusted-${p.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-600 text-white inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Adjusted → {local.qty}
                          </span>
                        )}
                        {!local && countedToday && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 inline-block">
                            Confirmed today
                          </span>
                        )}
                        {p.last_counted_at && (
                          <span className="text-[10px] text-gray-500 inline-block" data-testid={`check-last-${p.id}`}>
                            Last checked {new Date(p.last_counted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}{p.last_counted_by_name ? ` by ${p.last_counted_by_name}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase text-gray-500">Should be</div>
                      <div className="text-2xl font-bold" data-testid={`check-qty-${p.id}`}>{p.qty_on_hand}</div>
                    </div>
                    <div className="w-full sm:w-auto flex gap-2 sm:ml-2">
                      {local ? (
                        <Button
                          data-testid={`check-undo-${p.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => undoRow(p)}
                          className="flex-1 sm:flex-none"
                        >
                          <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo
                        </Button>
                      ) : (
                        <>
                          <Button
                            data-testid={`check-correct-${p.id}`}
                            size="sm"
                            onClick={() => confirmCorrect(p)}
                            className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Correct
                          </Button>
                          <Button
                            data-testid={`check-adjust-${p.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => setAdjustFor(p)}
                            className="flex-1 sm:flex-none border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Adjust
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {/* Sticky submit bar — visible at all times so the user can review the
          running tally + submit when the take is complete. */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t-2 shadow-2xl"
        style={{ background: '#0B0B0E', borderTopColor: '#D4A24B' }}
        data-testid="check-submit-bar"
      >
        <div className="max-w-[1500px] mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 text-white text-sm flex-wrap">
            <span data-testid="check-progress" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-white font-semibold">
              {pendingCounts.marked} / {pendingCounts.total} marked
            </span>
            <span data-testid="check-pending-confirmed" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-600/20 text-emerald-300 font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5" /> {pendingCounts.confirmed} correct
            </span>
            <span data-testid="check-pending-adjusted" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-600/20 text-amber-300 font-semibold">
              <AlertTriangle className="w-3.5 h-3.5" /> {pendingCounts.adjusted} adjusted
            </span>
          </div>
          <div className="flex-1 hidden sm:block text-white/60 text-xs">
            {pendingCounts.total === 0
              ? 'No products in this brand yet.'
              : pendingCounts.marked === 0
                ? `Mark every product in ${BRANDS.find(b => b.key === activeBrand)?.label} Correct or Adjust — the whole section must be done before Submit unlocks.`
                : pendingCounts.remaining > 0
                  ? `${pendingCounts.marked} marked, ${pendingCounts.remaining} still to go — finish every ${BRANDS.find(b => b.key === activeBrand)?.label} product to unlock Submit. Your progress is saved if you leave.`
                  : `All ${pendingCounts.total} items marked. Submit to email a single ${BRANDS.find(b => b.key === activeBrand)?.label} summary.`}
          </div>
          <Button
            data-testid="check-submit"
            onClick={submitAll}
            disabled={submitting || pendingCounts.marked === 0 || pendingCounts.remaining > 0}
            className="ml-auto disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: '#D4A24B', color: '#0B0B0E' }}
            title={pendingCounts.remaining > 0 ? `${pendingCounts.remaining} still to mark` : undefined}
          >
            <Send className="w-4 h-4 mr-2" />
            {submitting
              ? 'Submitting…'
              : pendingCounts.marked === 0
                ? 'Nothing marked yet'
                : pendingCounts.remaining > 0
                  ? `${pendingCounts.remaining} still to mark`
                  : `Submit ${BRANDS.find(b => b.key === activeBrand)?.label} check`}
          </Button>
        </div>
      </div>

      {adjustFor && (
        <DiscrepancyModal
          product={adjustFor}
          initial={pending[adjustFor.id]?.kind === 'adjusted' ? pending[adjustFor.id] : null}
          onClose={() => setAdjustFor(null)}
          onStage={(qty, reason) => {
            stageAdjustment(adjustFor, qty, reason);
            setAdjustFor(null);
          }}
        />
      )}
    </div>
  );
}

function DiscrepancyModal({ product, initial, onClose, onStage }) {
  const [qty, setQty] = useState(String(initial?.qty ?? product.qty_on_hand ?? 0));
  const [reason, setReason] = useState(initial?.reason || '');

  const submit = () => {
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n < 0) { toast.error('Counted quantity must be a non-negative number.'); return; }
    if (n === product.qty_on_hand) { toast.error('Counted qty matches the system — use "Correct" instead.'); return; }
    if (reason.trim().length < 2) { toast.error('A reason is required.'); return; }
    onStage(n, reason.trim());
  };

  const delta = parseInt(qty, 10) - (product.qty_on_hand || 0);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" /> Adjust stock</h2>
          <p className="text-xs text-gray-500 mt-1">{product.name} · system says <strong>{product.qty_on_hand}</strong></p>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label htmlFor="disc-qty" className="text-sm font-semibold">Actual counted quantity *</Label>
            <Input id="disc-qty" data-testid="discrepancy-qty" type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1" />
            {Number.isFinite(delta) && delta !== 0 && (
              <p className={`mt-1 text-xs font-semibold ${delta < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                Variance: {delta > 0 ? `+${delta}` : delta}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="disc-reason" className="text-sm font-semibold">Reason for the variance *</Label>
            <Textarea
              id="disc-reason"
              data-testid="discrepancy-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. 2 tubs found damaged in store, written off"
              className="mt-1"
            />
            <p className="text-[11px] text-gray-500 mt-1">Held locally until you click <strong>Submit stock take</strong>. A single summary email then goes to orders@ + dan@.</p>
          </div>
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="discrepancy-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} data-testid="discrepancy-submit" className="bg-amber-600 hover:bg-amber-700 text-white">Save to take</Button>
        </div>
      </div>
    </div>
  );
}
